import {
  Types as CoreTypes,
  Enums,
  eventTarget,
  getEnabledElement,
  utilities,
} from '@cornerstonejs/core';
import {
  Types as ToolsTypes,
  annotation,
  Enums as csToolsEnum,
  utilities as csToolsUtils,
} from '@cornerstonejs/tools';
import { useCurrentItem } from '@gleamer/gdata-extension';
import { OHIF, Internal } from '@gleamer/types';
import { utils as gleamerUtils } from '@gleamer/ui';
import {
  CommandsManager,
  DicomMetadataStore,
  DisplaySetService,
  HangingProtocolService,
  PanelService,
  ServicesManager,
  UINotificationService,
  ViewportGridService,
} from '@ohif/core';
import { useViewportGrid } from '@ohif/ui-next';
import { getDefaultStore } from 'jotai';
import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { vec2 } from 'gl-matrix';
import { drawPredictions } from '../tools/drawPredictions';
import { displayedPredictionsAtom } from './predictions.atoms';
import {
  useCurrentDetectionId,
  useDisplayPredictions,
  useDisplayedPredictions,
  usePredictionsTask,
  useSetPredictionsConfidences,
} from './predictions.hooks';
import { seriesOfItem, usePredictionsQuery } from './usePredictionQuery';
import {
  is2DDetection,
  is2DPointVisible,
  is3DPointVisible,
} from '../utils/predictions.utils';

type Detection = Internal.DetectionInternal;
type Prediction = Internal.PredictionInternal;

type UserAuthenticationService = OHIF.UserAuthenticationService;
type CornerstoneViewportService = OHIF.CornerstoneViewportService;
const csCoreEvents = Enums.Events;
const csToolsEvents = csToolsEnum.Events;

export const PredictionsContext = createContext<{
  isLoading: boolean;
  onDetectionSelection: (prediction: Prediction, detection: Detection) => void;
  onDetectionHover: (annotationId: string, hover: boolean) => void;
}>({
  isLoading: false,
  onDetectionSelection: (prediction: Prediction, detection: Detection) => {
    console.log('detection selection not implemented');
  },
  onDetectionHover: (annotationId: string, hover: boolean) => {
    console.log('detection hovering not implemented');
  },
});

export function usePredictions() {
  return useContext(PredictionsContext);
}

type PredictionsContextProviderProps = {
  servicesManager: ServicesManager;
  commandsManager: CommandsManager;
};

const isIStackViewPort = (
  viewport: CoreTypes.IViewport
): viewport is CoreTypes.IStackViewport => {
  return !!viewport && viewport.type === Enums.ViewportType.STACK;
};

export function PredictionsContextProvider({
  commandsManager,
  servicesManager,
  children,
}: PropsWithChildren<PredictionsContextProviderProps>) {
  const {
    cornerstoneViewportService,
    hangingProtocolService,
    uiNotificationService,
    viewportGridService,
    panelService,
    displaySetService,
  } = servicesManager.services as {
    userAuthenticationService: UserAuthenticationService;
    cornerstoneViewportService: CornerstoneViewportService;
    displaySetService: DisplaySetService;
    hangingProtocolService: HangingProtocolService;
    uiNotificationService: UINotificationService;
    viewportGridService: ViewportGridService;
    panelService: PanelService;
  };

  const labelTaskItem = useCurrentItem({ servicesManager });

  const task = usePredictionsTask();
  const predictionsConfiguration = task?.predictionsConfiguration;
  const patientId = labelTaskItem?.studies[0]?.PatientID;

  const predictionsQuery = usePredictionsQuery({
    servicesManager,
    patientId,
    taskId: task?.taskId,
    seriesInstanceUIDs: seriesOfItem(labelTaskItem),
  });
  // In react-query 4 the query stays `isLoading` while disabled: elsewhere
  // that is the wait for the item, but the bac mode never has one.
  const isLoading =
    gleamerUtils.getMode() === gleamerUtils.BAC_MODE_NAME
      ? predictionsQuery.isInitialLoading
      : predictionsQuery.isLoading;
  const [displayPredictions, setDisplayPredictions] = useDisplayPredictions();
  const setConfidences = useSetPredictionsConfidences();
  const [currentAnnotationId, setCurrentAnnotationId] = useCurrentDetectionId();
  const displayedPredictions = useDisplayedPredictions();
  const [currentSeriesInstanceUID, setCurrentSeriesInstanceUID] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (task?.predictionsConfiguration) {
      setConfidences([
        predictionsConfiguration?.minConfidence ?? 0,
        predictionsConfiguration?.maxConfidence ?? 100,
      ]);
    }
  }, [task?.predictionsConfiguration]);

  const [{ activeViewportId, viewports }] = useViewportGrid();

  // Bumped whenever a viewport gets its data, so predictions already in the
  // store when the viewer opens (Pre-annotations dropped in /local-bac) are drawn
  // once the Cornerstone viewport exists, not only on the next store change.
  const [viewportDataVersion, setViewportDataVersion] = useState(0);
  useEffect(() => {
    const { unsubscribe } = cornerstoneViewportService.subscribe(
      cornerstoneViewportService.EVENTS.VIEWPORT_DATA_CHANGED,
      () => setViewportDataVersion((version) => version + 1)
    );
    return unsubscribe;
  }, [cornerstoneViewportService]);

  // This prevents from displaying predictions on tasks that shouldn't display predictions.
  useEffect(() => {
    // If there is no task, we are in preview mode hence we won't disable predictions (they will be displayed),
    // we need to check the configuration for the other modes
    if (task?.name && !predictionsConfiguration && displayPredictions) {
      setDisplayPredictions(false);
    }
  }, [task?.name, predictionsConfiguration, displayPredictions]);

  const drawAnnotationsOnViewport = useCallback(
    (viewportId: string, generatedUids: Set<string>): void => {
      if (!displayedPredictions?.length) {
        return;
      }
      const viewport =
        cornerstoneViewportService.getCornerstoneViewport(viewportId);
      if (isIStackViewPort(viewport)) {
        const generatedAnnotations = drawPredictions(
          displayedPredictions && displayPredictions
            ? displayedPredictions
            : [],
          viewport,
          currentAnnotationId,
          predictionsConfiguration?.boxesColor
        );
        generatedAnnotations.forEach((annotation) => {
          generatedUids.add(annotation.annotationUID);
        });
      }
    },
    [
      displayedPredictions,
      currentAnnotationId,
      currentSeriesInstanceUID,
      predictionsConfiguration,
      displayPredictions,
    ]
  );

  const highlightDetection = useCallback(
    (annotationId: string, hover: boolean) => {
      setCurrentAnnotationId(hover ? annotationId : '');
    },
    [setCurrentAnnotationId]
  );

  const jumpToImage = useCallback(
    (viewportId: string, prediction: Prediction, detection: Detection) => {
      const viewport =
        cornerstoneViewportService.getCornerstoneViewport(viewportId);

      // Right after a page reload the Cleaning panel is restored as the active
      // panel and DetectionStatus auto-selects a detection, firing this jump
      // *while the layout is still settling* — so `getCornerstoneViewport` can
      // return a viewport that is being (re)created/disposed. Calling
      // `setImageIdIndex` on it throws CS3D 3.x's "stack viewport has been
      // destroyed", which the global ui-next ErrorBoundary surfaces as a
      // "Something went wrong" toast. Skip cleanly if the viewport isn't ready
      // (`getRenderingEngine()` is falsy once destroyed), and guard the command
      // itself against a viewport disposed between the check and the call — the
      // auto-jump is best-effort and re-runs on the next detection selection.
      if (!viewport || !viewport.getRenderingEngine()) {
        return;
      }

      const runJumpToImage = (imageIndex: number) => {
        // Navigate via `setImageIdIndex` directly — it RETURNS the promise so we can
        // catch it. (`utilities.jumpToSlice`/the OHIF `jumpToImage` command fire
        // `scroll`→`setImageIdIndex` fire-and-forget, so the async rejection escapes
        // uncaught.) `setImageIdIndex` throws synchronously if the viewport is already
        // destroyed, and on a large stack it gets disposed *while the image loads*,
        // rejecting the promise after the sync call — either way the global ui-next
        // ErrorBoundary would show a "Something went wrong" toast. Catch both. The
        // jump is best-effort; the STACK_VIEWPORT_NEW_STACK listener re-jumps once the
        // new stack is ready.
        try {
          Promise.resolve(
            (viewport as CoreTypes.IStackViewport).setImageIdIndex(imageIndex)
          ).catch((error) => {
            console.warn(
              '[predictions] jumpToImage skipped: viewport not ready',
              error
            );
          });
        } catch (error) {
          console.warn(
            '[predictions] jumpToImage skipped: viewport not ready',
            error
          );
        }
      };

      if (prediction.sopInstanceUID) {
        const displaySet = displaySetService
          .getDisplaySetsForSeries(prediction.seriesInstanceUID)
          .find((ds) => ds.StudyInstanceUID === prediction.studyInstanceUID);
        const imageIndex = displaySet.images.findIndex(
          (image) => image.SOPInstanceUID === prediction.sopInstanceUID
        );
        runJumpToImage(imageIndex);
      } else if (isIStackViewPort(viewport)) {
        const { x, y, z } = detection.points[0];
        const imageIndex = utilities.getClosestStackImageIndexForPoint(
          [x, y, z],
          viewport
        );
        runJumpToImage(imageIndex);
      }
    },
    [cornerstoneViewportService, displaySetService]
  );

  const resetViewport = useCallback(() => {
    commandsManager.runCommand('resetViewport');
  }, [commandsManager]);

  const onAnnotationSelected = async (detection: Detection) => {
    if (!detection) {
      return;
    }

    const viewport: CoreTypes.IStackViewport | CoreTypes.IVolumeViewport =
      cornerstoneViewportService.getCornerstoneViewport(activeViewportId);
    // Skip if the viewport is missing or being (re)created: on a cross-series
    // detection click `setDisplaySetsForViewports` has just switched the series, so
    // the viewport isn't ready yet and getViewReference()/worldToCanvas() throw —
    // which the global ui-next ErrorBoundary surfaces as a "Something went wrong"
    // toast the first time. Centering the detection in view is best-effort.
    if (!viewport || !viewport.getRenderingEngine()) {
      return;
    }

    try {
    const initialCamera = viewport.getCamera();
    if (!initialCamera) {
      return;
    }

    // Necessary to avoid modifying the original camera object.
    // And to batch the changes.
    const camera = { ...initialCamera };

    const { x: x1, y: y1, z: z1 } = detection.points[0];
    const { x: x2, y: y2, z: z2 } = detection.points[1];

    if (is2DDetection(detection)) {
      const imageId = viewport.getViewReference().referencedImageId;

      const upperLeft = { x: x1, y: y1 };
      const lowerRight = { x: x2, y: y2 };

      const isUpperLeftVisible = is2DPointVisible(upperLeft, viewport, imageId);
      const isLowerRightVisible = is2DPointVisible(
        lowerRight,
        viewport,
        imageId
      );

      // Check that all the points are visible in the current view.
      if (!isUpperLeftVisible || !isLowerRightVisible) {
        let newPosition = utilities.imageToWorldCoords(imageId, [
          (x1 + x2) / 2,
          (y1 + y2) / 2,
        ]);

        const instance = DicomMetadataStore.getInstanceByImageId(imageId);
        const { Rows: imageHeight, Columns: imageWidth } = instance;

        let imageCenterWorld = utilities.imageToWorldCoords(imageId, [
          imageWidth / 2,
          imageHeight / 2,
        ]);
        const canvasImageCenter = viewport.worldToCanvas(imageCenterWorld);
        const canvasNewPosition = viewport.worldToCanvas(newPosition);
        const panning = vec2.subtract(
          vec2.create(),
          canvasImageCenter,
          canvasNewPosition
        );

        viewport.setPan(panning as CoreTypes.Point2);

        const width = Math.abs(lowerRight.x - upperLeft.x);
        const height = Math.abs(lowerRight.y - upperLeft.y);

        const widthRatio = imageWidth / (width + 50);
        const heightRatio = imageHeight / (height + 50);
        // Zoom out if necessary
        viewport.setZoom(Math.min(widthRatio, heightRatio, viewport.getZoom()));
      }
    } else {
      const isPoint0Visible = is3DPointVisible(detection.points[0], viewport);
      const isPoint1Visible = is3DPointVisible(detection.points[1], viewport);

      if (!isPoint0Visible && !isPoint1Visible) {
        viewport.resetCamera();

        // FIXME - Fix zoom for 3D detections

        /*camera.focalPoint = [(x1 + x2) / 2, (y1 + y2) / 2, (z1 + z2) / 2];
        viewport.setCamera(camera);

        const imageId = viewport.getViewReference().referencedImageId;
        const instance = DicomMetadataStore.getInstanceByImageId(imageId);
        const { Rows: imageHeight, Columns: imageWidth } = instance;

        const upperRight = viewport.worldToCanvas([x1, y1, z1]);
        const lowerLeft = viewport.worldToCanvas([x2, y2, z2]);
        const width = Math.abs(lowerLeft[0] - upperRight[0]);
        const height = Math.abs(lowerLeft[1] - upperRight[1]);

        const widthRatio = imageWidth / (width + 50);
        const heightRatio = imageHeight / (height + 50);
        viewport.setZoom(Math.min(widthRatio, heightRatio, viewport.getZoom()));
        */
      }
    }
    } catch (error) {
      console.warn(
        '[predictions] center-on-detection skipped: viewport not ready',
        error
      );
    }
  };

  // Holds the in-flight cross-series re-jump registration so a new selection can
  // cancel the previous one. See `onSelectDetectionHandler` below.
  const pendingRejumpRef = useRef<AbortController | null>(null);

  const onSelectDetectionHandler = async (
    prediction: Prediction,
    detection: Detection
  ): Promise<void> => {
    const viewportId = activeViewportId;

    // Re-jump once the incoming stack is actually mounted.
    //
    // `setDisplaySetsForViewports` swaps the stack asynchronously and the new
    // stack starts at index 0, so the eager `jumpToImage` at the end of this
    // function is applied to the OUTGOING stack and is then discarded. That is
    // why selecting a detection on a series which had not been displayed yet
    // left the viewport on slice 1, and only worked after selecting another
    // detection and coming back — by then the series was already loaded and the
    // eager jump was enough.
    //
    // This safety net existed but had been dead since the CS3D 5 upgrade, in two
    // independent ways: `Enums.Events.STACK_VIEWPORT_NEW_STACK` was removed (the
    // lookup yielded `undefined`, so the listener was registered for an event
    // named "undefined"), and the replacement `VIEWPORT_NEW_IMAGE_SET` is
    // dispatched on the viewport ELEMENT, not on the global `eventTarget`. Only
    // the constant went away — `StackViewportNewStackEventDetail` still ships
    // and the payload is unchanged, which is why nothing failed loudly.
    //
    // Aborting the previous registration keeps at most one pending re-jump: the
    // listener only fires when a new stack is mounted, so selecting a detection
    // on the already-displayed series would otherwise leave it armed forever and
    // let a stale closure race the next jump with an earlier detection.
    pendingRejumpRef.current?.abort();
    const rejump = new AbortController();
    pendingRejumpRef.current = rejump;

    const element =
      cornerstoneViewportService.getCornerstoneViewport(viewportId)?.element;

    element?.addEventListener(
      Enums.Events.VIEWPORT_NEW_IMAGE_SET,
      ((({ detail }: CoreTypes.EventTypes.StackViewportNewStackEvent) => {
        if (viewportId !== detail.viewportId) {
          return;
        }
        jumpToImage(viewportId, prediction, detection);
        setCurrentSeriesInstanceUID(prediction.seriesInstanceUID);
      }) as unknown) as EventListener,
      { once: true, signal: rejump.signal }
    );

    let updatedViewports = [];
    try {
      updatedViewports = hangingProtocolService.getViewportsRequireUpdate(
        viewportId,
        displaySetService
          .getDisplaySetsForSeries(prediction.seriesInstanceUID)
          .find((ds) => ds.StudyInstanceUID === prediction.studyInstanceUID)
          ?.displaySetInstanceUID
      );
    } catch (error) {
      console.warn(error);
      uiNotificationService.show({
        title: 'Detection selection',
        message: 'The selected detection could not be added to the viewport.',
        type: 'info',
        duration: 3000,
      });
    }

    viewportGridService.setDisplaySetsForViewports(updatedViewports);
    setCurrentAnnotationId(detection.annotationId);
    jumpToImage(viewportId, prediction, detection);
    onAnnotationSelected(detection);
  };

  useEffect(() => {
    annotation.selection.setAnnotationSelected(
      currentAnnotationId,
      true,
      false
    );
  }, [currentAnnotationId]);

  const rectangleToolClickHandler = useCallback(
    (event: ToolsTypes.EventTypes.MouseDoubleClickEventType) => {
      const { element, currentPoints, event: nativeEvent } = event.detail;
      if (!element || !currentPoints) {
        return;
      }

      const enabledElement = getEnabledElement(element);
      if (!enabledElement) {
        console.warn('No enabled element found for element', element);
        return;
      }

      const nearbyToolData = csToolsUtils.getAnnotationNearPoint(
        element,
        currentPoints.canvas
      );

      if (nearbyToolData) {
        const typedNativeEvent = nativeEvent as Event;
        typedNativeEvent.preventDefault();
        typedNativeEvent.stopPropagation();

        const annotationId = getDefaultStore()
          .get(displayedPredictionsAtom)
          ?.flatMap((p) => p.detections)
          .map((d) => d.annotationId)
          .find((id) => id === nearbyToolData.annotationUID);

        if (annotationId) {
          const panelId = panelService
            .getPanels(panelService.PanelPosition.Right)
            .map((p) => p.id)
            .find((pId) => pId.includes('@gleamer/predictions-extension'));
          panelService.activatePanel(panelId, true);
          highlightDetection(annotationId, true);
        } else {
          console.log(
            'no detection for annotation id',
            nearbyToolData.annotationUID,
            displayedPredictions
          );
        }
      }
    },
    [highlightDetection]
  );

  useEffect(() => {
    const uids = new Set<string>();

    for (const [viewportId] of viewports) {
      try {
        drawAnnotationsOnViewport(viewportId, uids);
      } catch (error) {
        console.error(error);
      }
    }

    const renderingEngine = cornerstoneViewportService.getRenderingEngine();
    renderingEngine.render();

    function elementEnabledHandler(
      evt: CoreTypes.EventTypes.ElementEnabledEvent
    ) {
      const { element } = evt.detail;

      element.addEventListener(
        csToolsEvents.MOUSE_DOUBLE_CLICK,
        rectangleToolClickHandler
      );
    }

    const eventsHandlers = [
      {
        event: csCoreEvents.ELEMENT_ENABLED,
        handler: elementEnabledHandler.bind(null),
      },
    ];

    eventsHandlers.forEach(({ event, handler }) => {
      eventTarget.addEventListener(event, handler);
    });

    return () => {
      const annotationManager = annotation.state.getAnnotationManager();
      uids.forEach((uid) => {
        annotationManager.removeAnnotation(uid);
      });
      eventsHandlers.forEach(({ event, handler }) => {
        eventTarget.removeEventListener(event, handler);
      });
      const renderingEngine = cornerstoneViewportService.getRenderingEngine();
      renderingEngine.render();
    };
  }, [
    displayedPredictions,
    displayPredictions,
    cornerstoneViewportService,
    viewports,
    viewportDataVersion,
    drawAnnotationsOnViewport,
    rectangleToolClickHandler,
  ]);

  return (
    <PredictionsContext.Provider
      value={{
        isLoading,
        onDetectionSelection: onSelectDetectionHandler,
        onDetectionHover: highlightDetection,
      }}
    >
      {children}
    </PredictionsContext.Provider>
  );
}
